import { ReactNode, useEffect, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { SkuFacetState, SkuHierarchyTree as HierarchyTree } from './skuFacets';

export interface SkuHierarchyTreeProps {
  tree: HierarchyTree;
  facets: SkuFacetState;
  onSelectAll: () => void;
  onSelectUnassigned: () => void;
  onSelectCategory: (category: string) => void;
  onSelectSubCategory: (category: string, subCategory: string) => void;
}

function HierarchyRow({
  name,
  count,
  isActive,
  indent,
  chevron,
  onSelect,
}: {
  name: string;
  count: number;
  isActive: boolean;
  indent: number;
  chevron: ReactNode;
  onSelect: () => void;
}) {
  return (
    <div
      className={`flex items-center rounded-md ${
        isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      <span className='flex h-6 w-6 shrink-0 items-center justify-center'>
        {chevron}
      </span>
      <button
        type='button'
        onClick={onSelect}
        style={{ paddingLeft: `${indent}rem` }}
        className={`flex flex-1 items-center gap-1 py-1.5 pr-2.5 text-left text-sm ${
          isActive ? 'font-medium text-primary' : 'text-gray-700'
        }`}
      >
        <span className='flex-1 truncate'>{name}</span>
        <span className='text-xs'>{count}</span>
      </button>
    </div>
  );
}

export function SkuHierarchyTree({
  tree,
  facets,
  onSelectAll,
  onSelectUnassigned,
  onSelectCategory,
  onSelectSubCategory,
}: SkuHierarchyTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    facets.category ? new Set([facets.category]) : new Set()
  );

  const activeCategory = facets.category;
  useEffect(() => {
    if (!activeCategory) return;
    setExpanded((previous) =>
      previous.has(activeCategory)
        ? previous
        : new Set(previous).add(activeCategory)
    );
  }, [activeCategory]);

  const toggleExpanded = (category: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });

  const handleSelectCategory = (category: string) => {
    onSelectCategory(category);
    setExpanded((previous) => new Set(previous).add(category));
  };

  const chevronButton = (category: string, isExpanded: boolean) => (
    <button
      type='button'
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category}`}
      onClick={() => toggleExpanded(category)}
      className='rounded p-0.5 text-gray-400 hover:bg-gray-100'
    >
      <ChevronRightIcon
        className={`h-4 w-4 transition-transform ${
          isExpanded ? 'rotate-90' : ''
        }`}
      />
    </button>
  );

  return (
    <div className='flex flex-col gap-0.5'>
      <HierarchyRow
        name='All products'
        count={tree.total}
        isActive={!facets.unassigned && facets.category === null}
        indent={0}
        chevron={null}
        onSelect={onSelectAll}
      />

      {tree.categories.map((category) => {
        const isExpanded = expanded.has(category.name);
        return (
          <div key={category.name} className='flex flex-col gap-0.5'>
            <HierarchyRow
              name={category.name}
              count={category.count}
              isActive={
                facets.category === category.name && facets.subCategory === null
              }
              indent={0}
              chevron={
                category.subCategories.length
                  ? chevronButton(category.name, isExpanded)
                  : null
              }
              onSelect={() => handleSelectCategory(category.name)}
            />
            {isExpanded &&
              category.subCategories.map((subCategory) => (
                <HierarchyRow
                  key={subCategory.name}
                  name={subCategory.name}
                  count={subCategory.count}
                  isActive={
                    facets.category === category.name &&
                    facets.subCategory === subCategory.name
                  }
                  indent={1.25}
                  chevron={null}
                  onSelect={() =>
                    onSelectSubCategory(category.name, subCategory.name)
                  }
                />
              ))}
          </div>
        );
      })}

      {tree.unassigned > 0 && (
        <HierarchyRow
          name='Unassigned'
          count={tree.unassigned}
          isActive={facets.unassigned}
          indent={0}
          chevron={null}
          onSelect={onSelectUnassigned}
        />
      )}
    </div>
  );
}
